import React, { type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import { messages } from './messages';

const styles = cssMap({
	lastUpdated: {
		flexShrink: 0,
	},
});

export type SessionLastUpdatedProps = {
	/** Time of the latest state change, in epoch milliseconds. */
	lastStateChangeAt?: number;
	/** Preformatted content for consumers that do not have a raw timestamp. */
	lastUpdated?: ReactNode;
	testId?: string;
};

const COMPACT_TIME_UNITS: ReadonlyArray<
	readonly [message: (typeof messages)['compactMinute'], milliseconds: number]
> = [
	[messages.compactYear, 31_536_000_000],
	[messages.compactWeek, 604_800_000],
	[messages.compactDay, 86_400_000],
	[messages.compactHour, 3_600_000],
	[messages.compactMinute, 60_000],
];

const RelativeSessionTime = ({ timestamp }: { timestamp: number }): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const now = React.useMemo(() => Date.now(), []);
	const elapsed = Math.abs(now - timestamp);
	const [message, milliseconds] = COMPACT_TIME_UNITS.find(
		([, unitMilliseconds]) => elapsed >= unitMilliseconds,
	) ?? [messages.compactMinute, 60_000];
	const value = Math.max(1, Math.floor(elapsed / milliseconds));

	return <>{formatMessage(message, { value })}</>;
};

/** Renders a raw session timestamp as compact elapsed time. */
export const SessionLastUpdated = ({
	lastStateChangeAt,
	lastUpdated,
	testId,
}: SessionLastUpdatedProps): React.JSX.Element | null => {
	const content =
		lastStateChangeAt != null ? <RelativeSessionTime timestamp={lastStateChangeAt} /> : lastUpdated;

	if (content === undefined || content === null || content === false) {
		return null;
	}

	return (
		<Box as="span" xcss={styles.lastUpdated} testId={testId}>
			{content}
		</Box>
	);
};
